import { Tooltip } from '@/components/ui/Tooltip';
import ConfirmDeleteDialog from './ui/ConfirmDeleteDialog';
import { useSelector } from 'react-redux';
import React from 'react';
import { Pencil } from 'lucide-react';
import { Delete16Regular } from '@fluentui/react-icons';
import { Button } from '@/components/ui/Button';

type DataTableProps = {
  columns: Array<{
    key: string;
    title: string;
    list?: boolean;
  }>;
  data: Array<any>;
  onEdit?: (id: string) => void;
  onDelete?: (id: string, diffMulti: boolean) => void;
};

function DataTable(props: DataTableProps) {
  const permission = useSelector((state) => state.auth.pagePermission);
  const colCount = props.columns.length;

  const columns = () =>
    props.columns.map((column, i) => {
      const isLast = i === colCount - 1;
      if (column.key === 'id') {
        return (
          <th
            className={`px-3 py-2 w-[50px] text-left ${
              !isLast ? 'border-r border-neutral-150' : ''
            }`}
            key={column.key}
          />
        );
      }
      return (
        <th
          className={`px-3 py-2 text-left text-[11px] uppercase tracking-wider font-medium text-neutral-500 ${
            !isLast ? 'border-r border-neutral-150' : ''
          }`}
          key={column.key}
        >
          {column.title}
        </th>
      );
    });

  const rows = (data) => {
    if (!data) return [];
    return data.map((item, idx) => (
      <tr
        className={`hover:bg-neutral-25 transition-colors ${
          idx > 0 ? 'border-t border-neutral-150' : ''
        }`}
        key={item.id}
      >
        {props.columns.map((column, i) => {
          const isLast = i === colCount - 1;
          const borderClass = !isLast ? 'border-r border-neutral-150' : '';

          if (column.key === 'title') {
            return (
              <td
                className={`px-3 py-2 text-sm text-neutral-800 font-medium ${borderClass}`}
                colSpan={item.colSpan}
                key={column.key}
              >
                {item[column.key]}
              </td>
            );
          }
          if (column.list) {
            return (
              <td className={`px-3 py-2 text-sm text-neutral-700 ${borderClass}`} key={column.key}>
                {item[column.key]?.map((subItem) => subItem.title).join(', ')}
              </td>
            );
          }
          if (column.key === 'id') {
            return (
              <td key={column.key} className="px-3 py-2 w-[50px]">
                <div className="flex gap-1 justify-center items-center">
                  {props.onEdit && !item.diffMulti && permission === 2 && (
                    <Tooltip side="left" content="Editovat položku">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-neutral-300 hover:text-neutral-700"
                        data-testid={`data-table-edit-btn-${item.id}`}
                        onClick={() => props.onEdit(item.id)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </Tooltip>
                  )}
                  {props.onDelete && permission === 2 && (
                    <ConfirmDeleteDialog onConfirm={() => props.onDelete(item.id, item.diffMulti)}>
                      <Tooltip side="left" content="Odstranit položku">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-neutral-300 hover:text-red-600"
                          data-testid={`data-table-delete-btn-${item.id}`}
                        >
                          <Delete16Regular />
                        </Button>
                      </Tooltip>
                    </ConfirmDeleteDialog>
                  )}
                </div>
              </td>
            );
          }
          return (
            <td className={`px-3 py-2 text-sm text-neutral-700 ${borderClass}`} key={column.key}>
              {item[column.key]}
            </td>
          );
        })}
      </tr>
    ));
  };

  return (
    <div className="rounded border border-neutral-150 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-neutral-25 border-b border-neutral-150">
          <tr>{columns()}</tr>
        </thead>
        <tbody>{rows(props.data)}</tbody>
      </table>
    </div>
  );
}

export default DataTable;
